import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Auth2Service } from './auth/auth2.service';
import { ToastrService } from 'ngx-toastr';

/**
 * Signatures d'un module de route devenu introuvable, selon les navigateurs.
 *
 * Angular charge les modules de route par import() natif, le message n'est
 * donc plus le « Loading chunk N failed » de webpack et varie d'un moteur à
 * l'autre : Chrome dit « Failed to fetch dynamically imported module »,
 * Firefox « error loading dynamically imported module », Safari « Importing
 * a module script failed ». On garde ChunkLoadError pour les compilations
 * qui l'émettent encore.
 */
const CHUNK_ERROR_SIGNATURES = [
  'ChunkLoadError',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

/**
 * L'erreur signale-t-elle un module de route introuvable ?
 *
 * Exportée parce que deux endroits en ont besoin et qu'il ne doit y avoir
 * qu'une liste de signatures : ce gestionnaire, et AppComponent qui écoute les
 * NavigationError du routeur.
 */
export function isChunkLoadError(error): boolean {
  const description = (error?.name || '') + ' ' + (error?.message || '');
  return CHUNK_ERROR_SIGNATURES.some(signature => description.includes(signature));
}

/**
 * Extrait le message qu'une erreur serveur porte réellement.
 *
 * Le backend renvoie un ErrorDTO `{ exception, validationErrors }` pour tout ce
 * qui passe par son ExceptionHandler, mais ce n'est pas la seule forme qui
 * arrive jusqu'ici :
 *
 * - 404, 405, 415, JSON malformé... sont produits par Spring lui-même, sans
 *   passer par l'ExceptionHandler : le corps porte alors `message` / `error`.
 * - une erreur de proxy ou une page d'erreur renvoient du texte brut, pas du
 *   JSON : `error.error` est alors une chaîne.
 * - une coupure réseau ne fournit aucun corps du tout.
 *
 * L'ancienne version ne lisait que `error.error.exception`. Dans tous les
 * autres cas la notification s'affichait avec un corps `undefined` : l'écran
 * disait qu'une erreur s'était produite sans jamais dire laquelle.
 *
 * Exportée pour que les composants qui gèrent eux-mêmes leurs erreurs puissent
 * afficher le message du serveur plutôt qu'un texte générique.
 */
export function serverMessage(error): string {
  const body = error?.error;

  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }

  // ErrorDTO du backend, puis corps d'erreur standard de Spring.
  const message = body?.exception || body?.message || body?.error;
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }

  // Violations de contrainte, si jamais le résumé côté serveur manque.
  const validationErrors = body?.validationErrors;
  if (Array.isArray(validationErrors) && validationErrors.length) {
    const detail = validationErrors
      .map(e => e?.message)
      .filter(Boolean)
      .join(' ');
    if (detail) {
      return detail;
    }
  }

  if (error?.status === 0) {
    return 'Le serveur est injoignable. Vérifiez votre connexion, puis réessayez.';
  }

  const status = [error?.status, error?.statusText].filter(Boolean).join(' ');
  return status
    ? `Veuillez contacter le support si cette erreur réapparaît. (${status})`
    : 'Veuillez contacter le support si cette erreur réapparaît.';
}

/**
 * Created by dgo on 25.04.17.
 */
@Injectable()
export class MirHttpErrorHandler implements ErrorHandler {
  router: Router;

  /** Horodatage du dernier rechargement automatique, pour ne pas boucler. */
  private static readonly RELOAD_MARKER = 'mir.chunkReloadAt';

  constructor(private injector: Injector, private ngZone: NgZone) {}

  handleError(error) {
    // À traiter avant tout le reste : ce n'est pas une erreur applicative mais
    // une page devenue périmée, et le message générique n'aiderait personne.
    if (isChunkLoadError(error)) {
      this.reloadForNewVersion();
      return;
    }

    if (error?.status == 403 || error?.status == 401) {
      if (!this.injector.get(Auth2Service).isTokenExpired()) {
        this.showError(
          'Accès non autorisé',
          "Veuillez vérifier votre mot de passe ou si vous avez le droit d'accèder à cette page. (" +
            error.statusText +
            ')'
        );
      } else {
        //this.showError('Accès non autorisé', "Votre compte est actuellement bloqué");
      }
      this.injector.get(Auth2Service).logout();

      if (!this.router) {
        this.router = this.injector.get(Router);
      }
      this.router.navigate(['/signin']);

      return;
    }
    console.error(error);
    // this.injector.get(Auth2Service).logout(); // force cleanup token

    if (error) {
      this.showError('Cette opération a produit une erreur.', serverMessage(error));
    }

    return error;
  }

  /**
   * Recharge la page après une mise en production survenue pendant la session.
   *
   * Le contexte : l'application est découpée en modules chargés à la demande,
   * dont les noms de fichiers portent une empreinte du contenu. Après un
   * déploiement, l'onglet déjà ouvert continue de réclamer les anciens noms,
   * qui n'existent plus sur le serveur. La navigation échoue alors SANS RIEN
   * AFFICHER : l'utilisateur clique sur une entrée de menu et il ne se passe
   * rien. Seules les sections pas encore visitées sont touchées, d'où un
   * "certains menus ne répondent plus" difficile à interpréter.
   *
   * Recharger suffit à récupérer l'index et les bons noms de fichiers. On ne
   * le fait qu'une fois par tranche de 30 secondes : si le rechargement n'a
   * pas résolu le problème, le refaire en boucle rendrait l'application
   * inutilisable. Dans ce cas on retombe sur un message explicite.
   */
  private reloadForNewVersion() {
    // sessionStorage peut lever (Safari en navigation privée) : on ne laisse
    // pas le garde-fou faire échouer le gestionnaire d'erreurs lui-même. Sans
    // lui on recharge quand même, le pire cas restant préférable à un menu
    // définitivement muet.
    let lastReload = 0;
    try {
      lastReload = Number(sessionStorage.getItem(MirHttpErrorHandler.RELOAD_MARKER) || 0);
    } catch {
      lastReload = 0;
    }

    if (Date.now() - lastReload < 30000) {
      this.showError(
        'Chargement impossible',
        'Cette page n\'a pas pu être chargée. Rechargez l\'application ; si le problème persiste, contactez le support.'
      );
      return;
    }

    try {
      sessionStorage.setItem(MirHttpErrorHandler.RELOAD_MARKER, String(Date.now()));
    } catch {
      // Sans mémoire du dernier rechargement, on perd le garde-fou anti-boucle.
    }
    location.reload();
  }

  // https://stackoverflow.com/questions/44975477/angular2-ng-toasty-errorhandler
  /**
   * `ToastrService.error(message, title)` : le detail vient en PREMIER, le
   * titre en second -- le gabarit de la notification affiche le titre en gras
   * au-dessus du message. L'application appelait l'inverse partout, si bien
   * que la phrase longue s'affichait en titre et l'etiquette courte en dessous.
   */
  private showError(title: string, body: string) {
    this.ngZone.run(() => {
      this._toastrService.error(body, title);
    });
  }

  // Need to get ToastrService from injector rather than constructor injection to avoid cyclic dependency error
  // https://github.com/scttcper/ngx-toastr/issues/804#issuecomment-619987359
  private get _toastrService(): ToastrService {
    return this.injector.get(ToastrService);
  }
}
